import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';

const containerStyle = { maxWidth: 800, margin: '40px auto', padding: 24, border: '1px solid #ddd', borderRadius: 10, background: '#fafafa' };
const btnSecondary = { padding: '10px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' };
const btnSuccess = { padding: '10px 16px', background: '#28a745', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 500 };

const RAW_BASE = process.env.REACT_APP_API_BASE || '/api';
const API_BASE = RAW_BASE.replace(/["']/g, '').replace(/\/+$/, '');

export default function BandProgressionExam() {
  const { examId } = useParams();
  const [searchParams] = useSearchParams();
  const assignmentId = searchParams.get('assignmentId');
  const navigate = useNavigate();

  const [examConfig, setExamConfig] = useState(null);
  const [bands, setBands] = useState([]);
  const [nodes, setNodes] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [currentBandIndex, setCurrentBandIndex] = useState(0);
  const [currentNodeId, setCurrentNodeId] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isFinished, setIsFinished] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const startTimeRef = useRef(Date.now());
  const answersLogRef = useRef([]);
  const hasSubmittedRef = useRef(false);
  const cameFromWrongRef = useRef(false);
  
  // ✅ SYNCHRONOUS band tracking (avoids React state closure bugs)
  const bandProgressRef = useRef({});

  useEffect(() => { 
    console.log(`📥 BandProgressionExam mounted | examId: ${examId} | assignmentId: ${assignmentId}`);
    fetchStructure(); 
  }, [examId]);

  const fetchStructure = async () => {
    try {
      const res = await fetch(`${API_BASE}/student/exams/${examId}/band-structure`);
      if (!res.ok) throw new Error('Failed to load exam structure');
      const data = await res.json();
      
      console.log('🔍 Exam config loaded:', {
        passing_percentage: data.exam.passing_percentage,
        primary_points: data.exam.primary_points,
        backup_points: data.exam.backup_points,
        bands: data.bands.map(b => b.name)
      });

      setExamConfig(data.exam);
      setBands(data.bands);
      const nodeMap = {};
      data.nodes.forEach(n => { nodeMap[n.id] = n; });
      setNodes(nodeMap);
      
      // Initialize band progress tracking
      data.bands.forEach(b => {
        bandProgressRef.current[b.name] = { earned: 0, max: 0 };
      });

      if (data.bands.length > 0) setCurrentNodeId(data.bands[0].startNodeId);
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  const handleAnswer = (option) => {
    if (selectedAnswer) return;
    if (!examConfig) {
      console.warn('⚠️ Exam config not loaded yet. Ignoring answer.');
      return;
    }

    setSelectedAnswer(option);
    const currentNode = nodes[currentNodeId];
    const isCorrect = option === currentNode.question.correct;
    const currentBandName = currentNode.band;
    
    // ✅ Determine points synchronously
    const isPrimary = !cameFromWrongRef.current;
    const points = isPrimary 
      ? (Number(examConfig.primary_points) || 1) 
      : (Number(examConfig.backup_points) || 0.5);

    // ✅ Update ref immediately (no async state delays)
    const currentProgress = bandProgressRef.current[currentBandName] || { earned: 0, max: 0 };
    bandProgressRef.current[currentBandName] = {
      earned: currentProgress.earned + (isCorrect ? points : 0),
      max: currentProgress.max + points
    };

    console.log(`📝 Answered: ${isCorrect ? '✅ Correct' : '❌ Wrong'} | Band: ${currentBandName} | Points: +${points} (Max: +${points}) | Running Total: ${bandProgressRef.current[currentBandName].earned}/${bandProgressRef.current[currentBandName].max}`);

    // Log answer for final submission
    answersLogRef.current.push({
      flow_node_id: currentNode.id,
      question_id: currentNode.source_id,
      question_text: currentNode.question.text?.toString().slice(0, 500) || '',
      selected: option?.toString().charAt(0).toUpperCase() || 'X', // ✅ Ensure single uppercase char
      correct: currentNode.question.correct?.toString().charAt(0).toUpperCase() || 'X',
      is_correct: Boolean(isCorrect), // ✅ Ensure boolean
      band: currentNode.band?.toString() || 'Unknown',
      points: Number(points) || 0,
      is_primary: Boolean(isPrimary)
    });

    // Reset flag
    cameFromWrongRef.current = false;

    setTimeout(() => {
      determineNextStep(currentNode, isCorrect);
      setSelectedAnswer(null);
    }, 600);
  };

  const determineNextStep = (currentNode, isCorrect) => {
    let nextId = isCorrect ? currentNode.next_correct : currentNode.next_wrong;
    const nextNode = nextId ? nodes[nextId] : null;
    
    if (nextNode && nextNode.band === currentNode.band) {
      cameFromWrongRef.current = !isCorrect;
      setCurrentNodeId(nextId);
    } else {
      completeBand(currentNode.band);
    }
  };

  const completeBand = (completedBandName) => {
    const progress = bandProgressRef.current[completedBandName] || { earned: 0, max: 0 };
    const percentage = progress.max > 0 ? (progress.earned / progress.max) * 100 : 0;
    const required = Number(examConfig?.passing_percentage) || 60;
    
    console.log(`📊 Band "${completedBandName}" COMPLETED`);
    console.log(`   Earned: ${progress.earned} / Max: ${progress.max}`);
    console.log(`   Calculated: ${percentage.toFixed(2)}% | Required: ${required}%`);
    
    if (percentage >= required) {
      console.log(`   ✅ PASSED → Advancing to next band`);
      const nextBandIdx = currentBandIndex + 1;
      if (nextBandIdx < bands.length) {
        setCurrentBandIndex(nextBandIdx);
        setCurrentNodeId(bands[nextBandIdx].startNodeId);
      } else {
        console.log(`   ✅ Final band passed → Finishing exam`);
        setIsFinished(true);
      }
    } else {
      console.log(`   ❌ FAILED → Exam ends here (Band gate enforced)`);
      setIsFinished(true);
    }
  };

  // ✅ One-time submission
  useEffect(() => {
    if (isFinished && assignmentId && !hasSubmittedRef.current) {
      hasSubmittedRef.current = true;
      setIsSubmitting(true);
      
      const duration = Math.round((Date.now() - startTimeRef.current) / 1000);
      const answers = answersLogRef.current;
      const total = answers.length;
      const correct = answers.filter(a => a.is_correct).length;
      const score = total > 0 ? Math.round((correct / total) * 100) : 0;

      console.log('📤 Submitting results:', { assignmentId, score, correct, total, duration });

      // ✅ FIX: Get the token from localStorage
      const token = localStorage.getItem('token');
      
      fetch(`${API_BASE}/student/exams/${assignmentId}/submit-results`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` // ✅ FIX: Add the Authorization header
        },
        body: JSON.stringify({ score, correct_count: correct, total_questions: total, duration_seconds: duration, answers })
      })
      .then(async res => { 
        if (!res.ok) throw new Error(await res.json().catch(() => `Server ${res.status}`));
        return res.json(); 
      })
      .then(() => console.log('✅ Results saved successfully'))
      .catch(err => console.error('❌ Submission failed:', err))
      .finally(() => setIsSubmitting(false));
    }
  }, [isFinished, assignmentId]);

  if (loading) return <div style={{...containerStyle, textAlign: 'center'}}>⏳ Loading exam...</div>;
  if (error) return <div style={containerStyle}><p style={{color:'#721c24'}}>{error}</p><button style={btnSecondary} onClick={() => navigate('/student/dashboard')}>Return to Dashboard</button></div>;
  if (isSubmitting) return <div style={{...containerStyle, textAlign: 'center', marginTop: 60}}>⏳ Saving your results...</div>;

  if (isFinished) {
    return (
      <div style={{...containerStyle, textAlign: 'center', background: '#d4edda', border: '2px solid #28a745'}}>
        <div style={{ fontSize: 48, marginBottom: 10 }}>🎉</div>
        <h2 style={{ margin: '0 0 10px 0', color: '#155724' }}>Exam Completed!</h2>
        <p style={{ fontSize: 16, marginBottom: 25, color: '#155724', lineHeight: 1.5 }}>
          Great job finishing your assessment!<br/>
          Your teacher will review your answers shortly.
        </p>
        <button style={btnSuccess} onClick={() => navigate('/student/dashboard')}>Return to Dashboard</button>
      </div>
    );
  }

  const currentNode = nodes[currentNodeId];
  const currentBand = bands[currentBandIndex];
  if (!currentNode) return <div style={{...containerStyle, textAlign: 'center'}}>Loading next question...</div>;

  return (
    <div style={containerStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, borderBottom: '1px solid #eee', paddingBottom: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20 }}>{examConfig?.exam_name}</h2>
          <span style={{ fontSize: 14, color: '#666' }}>Current Band: <strong>{currentBand?.name}</strong></span>
        </div>
      </div>

      <div style={{ background: '#fff', padding: 20, borderRadius: 8, marginBottom: 20, border: '1px solid #ddd' }}>
        <h3 style={{ marginTop: 0, marginBottom: 20, fontSize: 16, lineHeight: 1.4 }}>{currentNode.question.text}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {Object.entries(currentNode.question.options).map(([opt, text]) => (
            <button key={opt} disabled={!!selectedAnswer} onClick={() => handleAnswer(opt)} style={{
              padding: 14, background: selectedAnswer === opt ? '#e7f1ff' : '#fff',
              border: selectedAnswer === opt ? '2px solid #007BFF' : '1px solid #ccc',
              borderRadius: 6, cursor: selectedAnswer ? 'default' : 'pointer', textAlign: 'left', fontSize: 14, transition: 'all 0.2s'
            }}>
              <strong>{opt}.</strong> {text}
            </button>
          ))}
        </div>
      </div>
      <div style={{ textAlign: 'center', fontSize: 12, color: '#888', marginTop: 10 }}>Select an option to continue automatically</div>
    </div>
  );
}